import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Lightweight @mention input.
 *
 * Wraps a textarea. When the user types `@`, a dropdown of matching members
 * appears anchored below the caret. Selecting one inserts `@Name ` and
 * records the user_id in the `mentions` set returned via `onChange`.
 *
 * Usage:
 *   <MentionInput
 *     value={text}
 *     mentions={mentions}                // Set<string> of user_ids
 *     onChange={(v, mentions) => {...}}
 *     members={[{user_id, name, email}]}
 *     onSubmit={() => answer()}
 *   />
 */
export default function MentionInput({
  value, mentions, onChange, members, onSubmit, placeholder, testid,
}) {
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [anchor, setAnchor] = useState({ top: 0, left: 0 });

  // Filter members for the dropdown.
  const matches = useMemo(() => {
    if (!open) return [];
    const q = (query || "").toLowerCase();
    return (members || [])
      .filter((m) => {
        const n = (m.name || "").toLowerCase();
        const e = (m.email || "").toLowerCase();
        return !q || n.includes(q) || e.includes(q);
      })
      .slice(0, 6);
  }, [open, query, members]);

  // Find the partial `@token` immediately preceding the caret.
  const updateMentionState = (text, caret) => {
    const upto = text.slice(0, caret);
    const m = upto.match(/(?:^|\s)@(\S*)$/);
    if (m) {
      setQuery(m[1]);
      setOpen(true);
      setActiveIdx(0);
      // Compute caret anchor for the dropdown.
      const r = inputRef.current?.getBoundingClientRect();
      const wr = wrapRef.current?.getBoundingClientRect();
      if (r && wr) {
        // We just place the dropdown below the input bottom-left rather than
        // tracking exact caret pixel (cheap + reliable).
        setAnchor({ top: r.bottom - wr.top + 4, left: 12 });
      }
    } else {
      setOpen(false);
    }
  };

  const onText = (e) => {
    const next = e.target.value;
    const caret = e.target.selectionStart || next.length;
    onChange(next, mentions);
    updateMentionState(next, caret);
  };

  // When inserting a chosen mention, also rebuild the mentions set from
  // tokens that survive in the text.
  const recomputeMentions = (text) => {
    const set = new Set();
    const byName = new Map();
    (members || []).forEach((m) => byName.set((m.name || "").toLowerCase(), m.user_id));
    // Match `@Words` where Words can include letters/digits/spaces if the
    // user picked a multi-word name — we matched on the dropdown selection so
    // we know exact names. For runtime parsing keep it simple: match the
    // longest known names first.
    const sortedNames = Array.from(byName.keys()).sort((a, b) => b.length - a.length);
    let lower = text.toLowerCase();
    for (const name of sortedNames) {
      const tag = `@${name}`;
      if (lower.includes(tag)) set.add(byName.get(name));
    }
    return set;
  };

  const pick = (m) => {
    const ta = inputRef.current;
    if (!ta) return;
    const caret = ta.selectionStart || value.length;
    const upto = value.slice(0, caret);
    const after = value.slice(caret);
    const newUpto = upto.replace(/(^|\s)@(\S*)$/, `$1@${m.name} `);
    const next = newUpto + after;
    const next_set = new Set(mentions);
    next_set.add(m.user_id);
    onChange(next, recomputeMentions(next).size ? recomputeMentions(next) : next_set);
    setOpen(false);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(newUpto.length, newUpto.length);
    });
  };

  const onKeyDown = (e) => {
    if (open && matches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % matches.length); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIdx((i) => (i - 1 + matches.length) % matches.length); return; }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pick(matches[activeIdx]);
        return;
      }
      if (e.key === "Escape") { e.preventDefault(); setOpen(false); return; }
    }
    if (e.key === "Enter" && !e.shiftKey && !open) {
      e.preventDefault();
      onSubmit?.();
    }
  };

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  return (
    <div ref={wrapRef} className="relative w-full">
      <textarea
        ref={inputRef}
        data-testid={testid}
        rows={2}
        value={value}
        onChange={onText}
        onKeyDown={onKeyDown}
        placeholder={placeholder || "Reply… Use @ to mention a member"}
        className="w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm resize-none"
      />
      {open && matches.length > 0 && (
        <div
          data-testid="mention-suggestions"
          style={{ top: anchor.top, left: anchor.left }}
          className="absolute z-30 w-64 max-h-64 overflow-auto bg-popover border border-border rounded-md shadow-xl py-1"
        >
          {matches.map((m, i) => (
            <button
              key={m.user_id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(m); }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full text-left px-3 py-1.5 text-sm transition ${i === activeIdx ? "bg-primary/10 text-foreground" : "hover:bg-secondary"}`}
            >
              <div className="font-medium truncate">@{m.name || m.email}</div>
              {m.email && m.name && (
                <div className="text-xs text-muted-foreground truncate">{m.email}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
